'use client';

import { useState } from 'react';
import { DataTable, type DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils';
import { Button, Icon, Card, showToast, Tooltip } from '@repo/ui';
import { ADMINISTRATIVE_FLAG_LABEL, AdministrativeFlag } from '@repo/types';

import { useEscalationReport } from '../../queries/escalationReportQuery';
import { reportingEscalationApi, type EscalationReportItem } from '../../api/reportingEscalationApi';

const cellClass = 'text-sm font-medium text-xtnd-dark dark:text-xtnd-light';

const columns: DataTableColumn<EscalationReportItem>[] = [
  {
    key: 'responsibleAttorney',
    header: 'Responsible Attorney',
    renderCell: (row) => (
      <div className={cellClass}>{row.responsibleAttorney || '-'}</div>
    ),
    defaultWidth: 180,
    isResizable: true,
  },
  {
    key: 'dateCollectionAttemptLogged',
    header: 'Date Collection Attempt Logged',
    renderCell: (row) => (
      <div className={cellClass}>
        {row.dateCollectionAttemptLogged
          ? dateUtil.formatDate(row.dateCollectionAttemptLogged, dateUtil.DateFormat.US)
          : '-'}
      </div>
    ),
    defaultWidth: 160,
    isResizable: true,
  },
  {
    key: 'administrativeSelection',
    header: 'Administrative Selection',
    renderCell: (row) => (
      <div className={cellClass}>
        {ADMINISTRATIVE_FLAG_LABEL[row.administrativeSelection as AdministrativeFlag] ?? row.administrativeSelection}
      </div>
    ),
    defaultWidth: 180,
    isResizable: true,
  },
  {
    key: 'escalatedBy',
    header: 'Escalated By',
    renderCell: (row) => (
      <div className={cellClass}>{row.escalatedBy || '-'}</div>
    ),
    defaultWidth: 200,
    isResizable: true,
  },
  {
    key: 'comments',
    header: 'Comments',
    renderCell: (row) => (
      <Tooltip content={row.comments || ''} position="top" disabled={!row.comments} maxWidth="max-w-md" contentClassName="whitespace-pre-wrap min-w-[500px] overflow-y-auto">
        <div
          className={`${cellClass} max-w-xs whitespace-pre-wrap break-words line-clamp-3`}
          onCopy={(e) => {
            if (row.comments) {
              e.preventDefault();
              e.clipboardData.setData('text/plain', row.comments);
            }
          }}
        >
          {row.comments || '-'}
        </div>
      </Tooltip>
    ),
    defaultWidth: 300,
    isResizable: true,
  },
  {
    key: 'invoiceNumber',
    header: 'Invoice Number',
    renderCell: (row) => (
      <div className={cellClass}>{row.invoiceNumber || '-'}</div>
    ),
    defaultWidth: 120,
    isResizable: true,
  },
  {
    key: 'outstandingArBalance',
    header: 'Outstanding AR Balance',
    align: 'right',
    renderCell: (row) => (
      <div className={cellClass}>{numberUtil.formatCurrency(row.outstandingArBalance)}</div>
    ),
    defaultWidth: 160,
    isResizable: true,
  },
  {
    key: 'matterTotalAr',
    header: 'Matter Total AR',
    align: 'right',
    renderCell: (row) => (
      <div className={cellClass}>{numberUtil.formatCurrency(row.matterTotalAr)}</div>
    ),
    defaultWidth: 140,
    isResizable: true,
  },
  {
    key: 'matterNumber',
    header: 'Matter Number',
    renderCell: (row) => (
      <div className={cellClass}>{row.matterNumber || '-'}</div>
    ),
    defaultWidth: 120,
    isResizable: true,
  },
  {
    key: 'matterName',
    header: 'Matter Name',
    renderCell: (row) => (
      <div className={`${cellClass} truncate max-w-xs`} title={row.matterName}>
        {row.matterName || '-'}
      </div>
    ),
    defaultWidth: 300,
    isResizable: true,
  },
  {
    key: 'clientName',
    header: 'Client Name',
    renderCell: (row) => (
      <div className={cellClass}>{row.clientName || '-'}</div>
    ),
    defaultWidth: 200,
    isResizable: true,
  },
  {
    key: 'originatingAttorney',
    header: 'Originating Attorney',
    renderCell: (row) => (
      <div className={cellClass}>{row.originatingAttorney || '-'}</div>
    ),
    defaultWidth: 180,
    isResizable: true,
  },
  {
    key: 'adjusterName',
    header: 'Adjuster Name',
    renderCell: (row) => (
      <div className={cellClass}>{row.adjusterName || '-'}</div>
    ),
    defaultWidth: 180,
    isResizable: true,
  },
  {
    key: 'adjusterEmail',
    header: 'Adjuster Email',
    renderCell: (row) => (
      <div className={cellClass}>{row.adjusterEmail || '-'}</div>
    ),
    defaultWidth: 220,
    isResizable: true,
  },
  // {
  //   key: 'adjusterPhoneNumber',
  //   header: 'Adjuster Phone Number',
  //   renderCell: (row) => (
  //     <div className={cellClass}>{row.adjusterPhoneNumber || '-'}</div>
  //   ),
  //   defaultWidth: 160,
  //   isResizable: true,
  // },
];

export default function EscalationReport() {
  const { data, isLoading, error, refetch } = useEscalationReport();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await reportingEscalationApi.exportExcel();
      showToast.success('Excel export downloaded successfully');
    } catch (err) {
      showToast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const total = data?.total ?? 0;

  return (
    <Card>
      <DataTable<EscalationReportItem>
        columns={columns}
        data={data ?? undefined}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        emptyState={{
          icon: 'HiExclamation',
          title: 'No escalated records found',
          description: 'There are no collection attempts escalated to Attorney or Leadership.',
        }}
        renderAction={() => (
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!data?.data?.length || isExporting}
            loading={isExporting}
            leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
          >
            Export Excel
          </Button>
        )}
        stickyHeader
        enableColumnResizing
        columnWidthsStorageKey="escalation-report"
        hidePagination
        loadingRowCount={25}
        loadingColumnCount={10}
      />
      {!isLoading && !error && total > 0 && (
        <div className="px-6 py-4 text-sm text-xtnd-dark-600 dark:text-xtnd-light-100 border-t border-xtnd-light dark:border-xtnd-dark-700">
          Showing {total.toLocaleString()} {total === 1 ? 'result' : 'results'}
        </div>
      )}
    </Card>
  );
}
